import type { OntosStore } from "../sqlite/index.js";
import type { RelationType, WalkStep } from "../types.js";
import type { ConversationUse, EvidenceKind, Trust } from "../enrich/types.js";

/**
 * §5B — decision archaeology. Walks the reasoning relations from an object,
 * returning short, evidence-first chains capped at ≤ 3 hops (cycle-guarded by the
 * store). Includes `builds-on` (lineage — this decision extends a prior one) and
 * `applies` (the rule that governed it), the two decision-cross-reference types
 * that answer "why did we decide this". `relates-to` is deliberately EXCLUDED: it
 * is a generic association, not a rationale, and would pull in navigational noise.
 *
 * TODO(phase1): shape the raw walk into chains with the backing evidence
 * snippets (the store already carries `evidence` on each relation).
 */
export function why(store: OntosStore, objectId: string, maxHops = 3): WalkStep[] {
  return store.traverse(objectId, {
    maxHops,
    rels: ["supersedes", "contradicts", "causes", "builds-on", "applies"],
  });
}

/**
 * A "possible (unverified)" connection touching an object — an LLM proposal that
 * is NOT in the trusted graph. This is the CONVERSATION / hedge path: it reads ALL
 * pending proposals (every trust level) and the agent USES them, hedged by
 * `conversationUse` (the 2026-06-20 policy: default-use + hedge, not confirm-gate).
 * SEPARATE from the RISK path (`guardEligibleProposals`): a wrong proposal is cheap
 * and correctable in dialogue, expensive in a guard — so the guard reads a strictly
 * narrower set. `why()` reads only the trusted graph, so it is never coloured here.
 *
 * Usage by `conversationUse` (the hedge): `high` — state it, lightly flagged;
 * `medium` — offer it as likely, not asserted; `low` — mention only if it changes
 * the answer, clearly tentative. `contested` (a prior user correction) → don't
 * re-assert. On an explicit "yes that's right" call `store.confirmProposal`; on a
 * correction `store.recordCorrection`; a salient, un-objected use can be logged with
 * `store.recordAcquiescence` (it earns trust over sessions). Highest trust first.
 */
export interface ProposalHint {
  readonly relId: string;
  readonly rel: RelationType;
  readonly sourceId: string;
  readonly targetId: string;
  /** Legacy surfacing tier (kept for back-compat; prefer `conversationUse` to hedge). */
  readonly trust: Trust;
  /** Read-time dialogue confidence (decay + derived≤parent applied when `nowISO` given). */
  readonly conversationUse: ConversationUse;
  /** How the proposal earned trust — lets the agent phrase the hedge honestly. */
  readonly evidenceKind: EvidenceKind;
  /** A prior user correction stands against it — do not re-assert. */
  readonly contested: boolean;
  readonly confidence: number;
  readonly evidencePath: string;
  /** ISO date last surfaced as a question (null = never) — for the agent's cooldown. */
  readonly askedAt: string | null;
}

/**
 * Conversation/hedge hints for an object. Reads ALL pending proposals. Pass `nowISO`
 * (the addon never reads the wall clock itself) to apply time decay + the derived≤parent
 * clamp to `conversationUse`; omit it to use the stored steady-state hedge.
 */
export function proposalHints(store: OntosStore, objectId: string, nowISO?: string): ProposalHint[] {
  return store.proposalsFor(objectId).map((p) => ({
    relId: p.relId,
    rel: p.rel,
    sourceId: p.sourceId,
    targetId: p.targetId,
    trust: p.trust,
    conversationUse: nowISO
      ? store.effectiveConversationUse(p.relId, nowISO) ?? p.conversationUse ?? "low"
      : p.conversationUse ?? "low",
    evidenceKind: p.evidenceKind ?? "self",
    contested: p.contested ?? false,
    confidence: p.confidence,
    evidencePath: p.evidencePath,
    askedAt: p.askedAt ?? null,
  }));
}

/**
 * A guard-eligible proposal touching an object — the RISK path. UNLIKE a
 * {@link ProposalHint} this carries no `conversationUse`: it is not a hedge but a hard
 * signal a risk guard may act on (pre-commit / deploy / recurring-failure). Only
 * deterministic/explicit, record-sourced, uncontested, parent-eligible proposals reach
 * here (see `store.guardEligibleProposalsFor`). Acquiescence / self / cross_model /
 * conversation knowledge can NEVER appear — the core safety line of the trust model.
 */
export interface GuardEligibleHint {
  readonly relId: string;
  readonly rel: RelationType;
  readonly sourceId: string;
  readonly targetId: string;
  readonly evidenceKind: EvidenceKind;
  readonly confidence: number;
  readonly evidencePath: string;
}

/**
 * The RISK path's proposal reader. Returns ONLY guard-eligible proposals — never the
 * conversation/acquiescence knowledge `proposalHints` surfaces. A risk guard that wants
 * to factor in (not-yet-confirmed) extracted knowledge reads THIS, never `proposalHints`.
 */
export function guardEligibleProposals(store: OntosStore, objectId: string): GuardEligibleHint[] {
  return store.guardEligibleProposalsFor(objectId).map((p) => ({
    relId: p.relId,
    rel: p.rel,
    sourceId: p.sourceId,
    targetId: p.targetId,
    evidenceKind: p.evidenceKind ?? "self",
    confidence: p.confidence,
    evidencePath: p.evidencePath,
  }));
}

/** A remedy the trusted graph attaches to a failure class (covering rule / resolving action). */
export interface RemedyRef {
  readonly id: string;
  readonly label: string;
}

export interface GuardHit {
  readonly failureId: string;
  readonly recurrence: number;
  /** Rules the class is `covered-by` (trusted graph; absent on older builds). */
  readonly coveredBy?: readonly RemedyRef[];
  /** Actions/guards the class is `resolved-by` (trusted graph; absent on older builds). */
  readonly resolvedBy?: readonly RemedyRef[];
}

/**
 * §5A — recurring-failure guard. Given a situation already resolved to a failure
 * object id (the §6 trigger surface does that resolution), report whether it
 * recurs at/over the threshold — WITH the remedy the graph knows (the covering
 * rule / resolving action), so the caller can say "you've hit this before, and
 * it's guarded by X" instead of just "it recurred".
 *
 * SAFETY: counts recurrence over the TRUSTED `relations` graph only (deterministic
 * recurs-as edges) — it never reads proposals, so no acquiescence/conversation
 * knowledge can inflate a risk warning. Remedies come from the same trusted graph
 * (`covered-by` → rule, `resolved-by` → action), direction-exact (`neighbors` is
 * outgoing-only) and type-filtered, deduped, sorted by id for deterministic output.
 * A risk path that wants extracted-but-unconfirmed signal reads
 * `guardEligibleProposals` (guard_eligible only), never `proposalHints`.
 */
export function recurringFailureGuard(
  store: OntosStore,
  failureId: string,
  threshold = 2,
): GuardHit | null {
  // Recurrence is counted on the canonical CLASS id. Resolve an occurrence to
  // its class STRUCTURALLY — follow its recurs-as edge — rather than by string
  // surgery on "@" (a recurrence_key could itself contain "@"). A class id has
  // no outgoing recurs-as edge, so it resolves to itself.
  const viaEdge = store.neighbors(failureId, ["recurs-as"]).find((r) => r.sourceId === failureId);
  const classId = viaEdge ? viaEdge.targetId : failureId;
  const recurrence = store.recurrenceCount(classId);
  if (recurrence < threshold) return null;
  return {
    failureId: classId,
    recurrence,
    coveredBy: remediesOf(store, classId, "covered-by", "rule"),
    resolvedBy: remediesOf(store, classId, "resolved-by", "action"),
  };
}

/**
 * Outgoing `rel` edges from a failure class → remedy refs. FKs guarantee the
 * target object row exists; a target whose type does not match the relation's
 * range (a mis-typed edge) is dropped rather than shown as a false remedy.
 */
function remediesOf(
  store: OntosStore,
  classId: string,
  rel: RelationType,
  targetType: string,
): RemedyRef[] {
  const seen = new Set<string>();
  const out: RemedyRef[] = [];
  for (const edge of store.neighbors(classId, [rel])) {
    if (seen.has(edge.targetId)) continue;
    seen.add(edge.targetId);
    const obj = store.getObject(edge.targetId);
    if (obj && obj.type !== targetType) continue;
    out.push({ id: edge.targetId, label: obj?.label ?? edge.targetId });
  }
  out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}
